import { useCallback, useEffect, useState } from 'react';
import { getRelativeEpoch } from '../utils/date-helper';

export const usePeriod = (defaultStart?: number, defaultEnd?: number) => {
  const [state, setState] = useState<IState>({});
  const [timeFrame, setTimeFrame] = useState<TimeFramePeriod>('begin');

  useEffect(() => {
    if (!defaultStart || !defaultEnd) return;

    setState({
      calculatedPeriod: {
        startDate: defaultStart,
        endDate: defaultEnd,
      },
      firstAndLastQuoteDate: {
        startDate: defaultStart,
        endDate: defaultEnd,
      },
    });
  }, [defaultStart, defaultEnd]);

  const calcPeriod = useCallback((periodType: string, periods: IPeriod[]) => {
    const periodUseCase = new PeriodUseCase();
    const calculatedPeriod = periodUseCase.calcPeriod(periodType, periods);

    setState({
      calculatedPeriod,
      firstAndLastQuoteDate: periodUseCase.calcPeriod('otimo', periods),
    });

    return {
      startDate: calculatedPeriod?.startDate,
      endDate: calculatedPeriod?.endDate,
    };
  }, []);

  const createPeriodDatesFromSeries = (series: any[]): IPeriod[] => {
    return series
      .map((item) => {
        const startDate = item?.data[0]?.[0];
        const endDate = item?.data[item?.data?.length - 1]?.[0];

        return {
          startDate,
          endDate,
        };
      })
      .filter(Boolean);
  };

  const changeTimeFrame = useCallback((timeframe: TimeFramePeriod) => {
    setTimeFrame(timeframe);
  }, []);

  return {
    calculatedPeriod: state.calculatedPeriod,
    firstAndLastQuoteDate: state.firstAndLastQuoteDate,
    calcPeriod,
    createPeriodDatesFromSeries,
    changeTimeFrame,
    timeFrame,
  };
};

interface IState {
  calculatedPeriod?: IPeriod;
  firstAndLastQuoteDate?: IPeriod;
}

export interface IPeriod {
  type?: IPeriodType;
  value?: string;
  startDate: number;
  endDate: number;
}

export type IPeriodType =
  | 'otimo'
  | '1'
  | '3'
  | '6'
  | '12'
  | '24'
  | '36'
  | '48'
  | string;

export type TimeFramePeriod =
  'last_3_months' |
  'last_6_months' |
  'last_12_months' |
  'last_24_months' |
  'last_36_months' |
  'ytd' |
  'mtd' |
  'begin';

class PeriodUseCase {
  private maxStartDate = (periods: IPeriod[]): number => {
    const startDates = periods.map((assetDate) => assetDate?.startDate);
    return Math.max(...startDates);
  };

  private minEndDate = (periods: IPeriod[]): number => {
    const endDates = periods.map((assetDate) => assetDate?.endDate);
    return Math.min(...endDates);
  };

  calcPeriod = (periodType: string = 'otimo', periods: IPeriod[]): IPeriod => {
    const periodTypeWithoutOldValue = periodType.replace('C:', '');

    const defaultValues = [1, 3, 6, 12, 24, 36, 48];

    if (
      !isNaN(Number(periodTypeWithoutOldValue)) &&
      !defaultValues.includes(Number(periodTypeWithoutOldValue))
    ) {
      return {
        type: 'months',
        value: periodTypeWithoutOldValue,
        startDate: getRelativeEpoch(
          Number(periodTypeWithoutOldValue),
          this.minEndDate(periods)
        ),
        endDate: this.minEndDate(periods),
      };
    }

    switch (periodTypeWithoutOldValue) {
      case 'otimo':
        return {
          type: 'otimo',
          startDate: this.maxStartDate(periods),
          endDate: this.minEndDate(periods),
        };

      case '1':
        return {
          type: '1',
          startDate: getRelativeEpoch(1, this.minEndDate(periods)),
          endDate: this.minEndDate(periods),
        };

      case '3':
        return {
          type: '3',
          startDate: getRelativeEpoch(3, this.minEndDate(periods)),
          endDate: this.minEndDate(periods),
        };

      case '6':
        return {
          type: '6',
          startDate: getRelativeEpoch(6, this.minEndDate(periods)),
          endDate: this.minEndDate(periods),
        };

      case '12':
        return {
          type: '12',
          startDate: getRelativeEpoch(12, this.minEndDate(periods)),
          endDate: this.minEndDate(periods),
        };

      case '24':
        return {
          type: '24',
          startDate: getRelativeEpoch(24, this.minEndDate(periods)),
          endDate: this.minEndDate(periods),
        };

      case '36':
        return {
          type: '36',
          startDate: getRelativeEpoch(36, this.minEndDate(periods)),
          endDate: this.minEndDate(periods),
        };

      case '48':
        return {
          type: '48',
          startDate: getRelativeEpoch(48, this.minEndDate(periods)),
          endDate: this.minEndDate(periods),
        };

      default: {
        const [startDate, endDate] = periodTypeWithoutOldValue.split('-');

        return {
          type: 'custom',
          startDate: Number(startDate),
          endDate: Number(endDate),
        };
      }
    }
  };
}
